import { eTlfItemType } from "functions/api/enums";
import { isArsMasterReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { applyVariantPatches } from "functions/tlf/applyVariantPatches";
import { pickVariantUpdateable } from "functions/tlf/pickVariantUpdateable";
import { commitActivePatch } from "./commitActivePatch";
import { getVariantPatchContext } from "./getVariantPatchContext";
import { ITlfActionRemoveVariantPatch, TlfContextProps, UPDATE_TLF } from "./interface";
import { tlf_updateTlf } from "./tlf_updateTlf";
import { toArsTlfSnapshot } from "./toArsTlfSnapshot";
import { updateVariantPatchSelection } from "./updateVariantPatchSelection";

/**
 * Removes a catalogue patch and recomposes the remaining applied patches.
 *
 * @param state - Current TLF state and master snapshot used to restore removed changes
 * @param action - Patch to delete and reducer action origin for history tracking
 * @returns Updated state with cleaned references, or unchanged state if removal is invalid
 */
export const tlf_removeVariantPatch = (
  state: TlfContextProps,
  action: ITlfActionRemoveVariantPatch,
): TlfContextProps => {
  if (state.readOnly || !isArsMasterReadDto(state.tlfData)) return state;
  const current = state.tlfData;
  if (!Object.hasOwn(current.allVariantPatches, action.patchName)) return state;
  const master =
    getVariantPatchContext(state).master ??
    (state.appliedVariantPatches.length === 0 ? toArsTlfSnapshot(current) : null);
  if (master === null) return state;
  const { catalog } = commitActivePatch(
    state.activeVariantPatchName === action.patchName
      ? { ...state, activeVariantPatchName: null }
      : state,
    action.calledFrom,
  );
  if (catalog === null) return state;
  const allVariantPatches = { ...catalog };
  delete allVariantPatches[action.patchName];
  const appliedVariantPatches = state.appliedVariantPatches.filter(
    (name) => name !== action.patchName,
  );
  const variantTemplates = current.variantTemplates.map((template) => ({
    ...template,
    variantPatches: template.variantPatches.filter((name) => name !== action.patchName),
  }));
  const composed = applyVariantPatches(
    master,
    { allVariantPatches, variantTemplates },
    appliedVariantPatches,
  );
  if (!composed.success) return state;
  const tlfData =
    composed.data.type === eTlfItemType.arsListing
      ? {
          type: composed.data.type,
          ...pickVariantUpdateable(composed.data),
          allVariantPatches,
          variantTemplates,
        }
      : {
          type: composed.data.type,
          ...pickVariantUpdateable(composed.data),
          allVariantPatches,
          variantTemplates,
        };
  const newState = tlf_updateTlf(state, {
    operation: UPDATE_TLF,
    calledFrom: action.calledFrom,
    tlfData,
  });
  return updateVariantPatchSelection(
    {
      ...newState,
      activeVariantPatchInvalidOps: [],
    },
    appliedVariantPatches,
    state.activeVariantPatchName === action.patchName
      ? (appliedVariantPatches[0] ?? null)
      : state.activeVariantPatchName,
  );
};
